import React, { useState, useEffect } from "react";
import cn from "clsx";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  formatDate,
  formatDateTime,
  formatTime,
  useTimeFormat,
} from "helpers/timeFormat";
import getSymbolFromCurrency from "currency-symbol-map";

import { UserCashbackItem } from "store/players/list/slice";
import { playersListLimits } from "configs/limits";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import Pagination from "components/Pagination";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import styles from "./ListCashbacksTab.module.scss";

const cashbackStatusClassMap: Record<string, string> = {
  expired: "badge--neutral",
  completed: "badge--success",
  pending: "badge--warning",
  processing: "badge--warning",
  failed: "badge--danger",
  canceled: "badge--danger",
  cancelled: "badge--danger",
};

const getCashbackStatusClass = (status: string) => {
  const statusKey = status.trim().toLowerCase();

  return (
    cashbackStatusClassMap[statusKey] ??
    `badge--${statusKey.replaceAll(/\s+/g, "-")}`
  );
};

const ListCashbacksTab: React.FC = () => {
  const timeFormat = useTimeFormat();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [list, setList] = useState<UserCashbackItem[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [currentLimit, setCurrentLimit] = useState(playersListLimits[0]);
  const [isFetched, setIsFetched] = useState(false);

  const handleLoadList = async () => {
    setIsFetched(false);
    try {
      const result = await instance.get(
        `${apiRoutes.playerCashbacks}/${searchParams.get("entityId")}?limit=${currentLimit}&offset=${activePage - 1}`,
      );
      setList(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch {
      console.log("handleLoadList error");
    }
    setIsFetched(true);
  };

  useEffect(() => {
    handleLoadList();
  }, [activePage, currentLimit]);

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage(activePage - 1);
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      setActivePage(activePage + 1);
    }
  };

  return (
    <div className={`${styles.cashbacks} player-content-wrapper`}>
      {isFetched ? (
        <>
          <div className={`${styles.table} inner-table-wrapper`}>
            <table>
              <thead>
                <tr>
                  <th>{t("title")}</th>
                  <th>{t("image")}</th>
                  <th>{t("type/level")}</th>
                  <th>{t("losses")}</th>
                  <th>{t("min/max")}</th>
                  <th>{t("percentages")}</th>
                  <th>{t("cashback")}</th>
                  <th>{t("status")}</th>
                  <th className={styles.calculatedDate}>
                    {t("calculatedDate")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.map((el) => (
                  <tr key={el.id}>
                    <td>{el.title}</td>
                    <td className={styles.imageCell}>
                      <img src={el.image} alt="" />
                    </td>
                    <td>
                      {el.type} / {el.level}
                    </td>
                    <td>{el.netLosses}</td>
                    <td>
                      {el.min} / {el.max}
                    </td>
                    <td>{el.percentage}</td>
                    <td>
                      {getSymbolFromCurrency(el.currency)} {el.cashback}
                    </td>
                    <td className={styles.statusCell}>
                      <span
                        className={cn(
                          "badge",
                          getCashbackStatusClass(el.status),
                        )}
                      >
                        {el.status}
                      </span>
                    </td>
                    <td
                      className={`date ${styles.calculatedDate}`}
                      title={formatDateTime(el.calculatedDate, timeFormat)}
                    >
                      <span>{formatDate(el.calculatedDate)}</span>
                      <span>{formatTime(el.calculatedDate, timeFormat)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={playersListLimits}
            handleLimitClick={(limit) => {
              setCurrentLimit(limit);
              setActivePage(1);
            }}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
          />
        </>
      ) : (
        <div className={styles.table}>
          <ModalTabSkeleton />
        </div>
      )}
    </div>
  );
};

export default ListCashbacksTab;
